import { clean } from './format';
import { captionOf } from './lightbox';
import { byDist, type Poi } from './places';
import { createStore } from './store';

export type GalleryCat = 'homes' | 'community' | 'neighborhood';
export const GALLERY_CATS: GalleryCat[] = ['homes', 'community', 'neighborhood'];
export const CAT_LABEL: Record<GalleryCat, string> = { homes: 'Homes', community: 'Community', neighborhood: 'Neighborhood' };

export interface GalleryTile {
  src: string;
  alt: string;
  caption: string;
  desc: string;
  cat: GalleryCat;
}

interface Photo {
  image?: string;
  original_url?: string;
  alt_text?: string;
  title?: string;
  description?: string;
}

/** MultiHub titles photos "Apartment" (inside a home) or "Community"; untitled ones are read from the alt text. */
const INTERIOR = /kitchen|bedroom|bathroom|living (room|area)|dining|closet|home office|laundry|hallway|staircase|primary suite|nursery|interior/i;
const photoCat = (g: Photo): GalleryCat =>
  /^apartment|^home|^interior/i.test(g.title || '') ? 'homes' : /^community|^amenit/i.test(g.title || '') ? 'community' : INTERIOR.test(g.alt_text || '') ? 'homes' : 'community';

export function photoTiles(list: Photo[]): GalleryTile[] {
  return list
    .map((g) => ({ g, src: clean(g.image) || clean(g.original_url) || '' }))
    .filter((x) => x.src)
    .map(({ g, src }) => ({ src, alt: g.alt_text || '', caption: captionOf(g), desc: clean(g.description) || g.alt_text || '', cat: photoCat(g) }));
}

/** Neighborhood places with their own photo, nearest first. */
export function placeTiles(pois: Poi[]): GalleryTile[] {
  return pois
    .filter((x) => clean(x.poi_featured_image))
    .slice()
    .sort(byDist)
    .map((x) => ({ src: x.poi_featured_image!, alt: x.poi_name || '', caption: x.poi_name || '', desc: clean(x.poi_short_description) || '', cat: 'neighborhood' as const }));
}

export interface GalleryState {
  cat: GalleryCat | 'all';
}

export const galleryStore = createStore<GalleryState>({ cat: 'all' });

/** Jump links ("Homes ↓") use #homes / #community / #neighborhood; the filter bar is the scroll target. */
export function catFromHash(hash: string): GalleryCat | null {
  const h = hash.replace(/^#/, '') as GalleryCat;
  return GALLERY_CATS.includes(h) ? h : null;
}
